import { FocusEvent, Fragment, forwardRef, useCallback, useEffect, useRef, useState } from "react";

import { LoadingGroup } from "../../general";
import { Stack } from "../../layout";

import { useId } from "../../hooks/useId";
import { getBasePropsFromHtmlProps, getHtmlPropsFromBaseProps } from "../../utilities";
import { EmptyArray } from "../../utilities/EmptyArray";
import { isDescendentOf } from "../../utilities/isDescendentOf";
import { SharedCheckbox } from "../Checkbox/SharedCheckbox";
import { useInputDisabled, useInputReadonly } from "../InputGroup";
import { ValidityGroup, useValidity } from "../ValidityGroup";
import { GroupLayoutWrapper, InputContainer, NoPaddingFormComponents } from "../shared";
import { CheckboxGroupProps } from "./CheckboxGroup";
import { Placeholder } from "./Placeholder";
import { getItems, getOptions } from "./helpers";
import { createComponentStyleHook } from "../../tokens";
import tokenStyles from "../Checkbox/styles.module.css";
import legacyStyles from "../Checkbox/styles.module.scss";

const useResolvedStyles = createComponentStyleHook<InputProps>({
  legacyStyles,
  tokenStyles,
  componentTokens: "checkbox",
});

/**
 * The `CheckboxGroupInternal` component properties.
 */
export interface InputProps extends CheckboxGroupProps, NoPaddingFormComponents {
  /**
   * Indicates whether the input is required.
   */
  readonly required?: boolean;
}

/**
 * A form input that presents a group of series of checkboxes which can be toggled on and off.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(props, ref) {
  const {
    required = false,
    children,
    label,
    tooltip,
    instructions,
    orientation = "vertical",
    hideInstructions,
    hideLabel,
    autoFocus = false,
    defaultValue = null,
    unpadded = false,
    onFocusEnter,
    onFocusExit,
    onChange,
  } = props;

  const { value = defaultValue } = props;
  const { mode, styles, StyleModeBoundary } = useResolvedStyles(props);

  const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: [styles.CheckboxGroup, "checkbox-group"],
    testIdPrefix: "checkbox-group",
  });

  const id = useId();
  const groupDisabled = useInputDisabled(props);
  const groupReadOnly = useInputReadonly(props);
  const [invalid] = useValidity();

  const items = getItems(children);

  const [valueSet, setValueSet] = useState(() => new Set(value ?? EmptyArray));
  const [focused, setFocused] = useState(false);

  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setValueSet(new Set(value ?? EmptyArray));
  }, [value]);

  const changeHandler = useCallback(
    (id: string | number, checked: boolean | "indeterminate" | null) => {
      let newValue: ReadonlyArray<string | number> = value ?? EmptyArray;

      if (checked && !newValue.includes(id)) {
        newValue = [...newValue, id];
      } else if (!checked) {
        newValue = newValue.filter((item) => item !== id);
      }

      onChange?.(newValue, value);
    },
    [value, onChange],
  );

  const groupChangeHandler = useCallback(
    (ids: ReadonlyArray<string | number>, checked: boolean | "indeterminate" | null) => {
      let newValue: ReadonlyArray<string | number> = value ?? EmptyArray;

      for (const id of ids) {
        if (checked && !newValue.includes(id)) {
          newValue = [...newValue, id];
        } else if (!checked) {
          newValue = newValue.filter((item) => item !== id);
        }
      }

      onChange?.(newValue, value);
    },
    [value, onChange],
  );

  const focusHandler = useCallback(() => {
    if (!focused) {
      setFocused(true);
      onFocusEnter?.();
    }
  }, [focused, onFocusEnter]);

  const blurHandler = useCallback(
    (ev: FocusEvent) => {
      const { relatedTarget } = ev;
      const { current: root } = rootRef;

      if (relatedTarget && root && isDescendentOf(root, relatedTarget)) return;

      if (focused) {
        setFocused(false);
        onFocusExit?.();
      }
    },
    [focused, onFocusExit],
  );

  return (
    <StyleModeBoundary mode={mode}>
      <LoadingGroup.Placeholder fallback={<Placeholder optionCount={items.length || 2} />}>
        <InputContainer
          {...getBasePropsFromHtmlProps(baseHtmlProps)}
          id={id}
          label={label}
          disabled={groupDisabled}
          readOnly={groupReadOnly}
          tooltip={tooltip}
          required={!groupDisabled && !groupReadOnly && required}
          instructions={instructions}
          hideInstructions={hideInstructions}
          hideLabel={hideLabel}
        >
          <div
            ref={rootRef}
            className="group-container"
            role="group"
            aria-labelledby={label ? `${id}-label` : undefined}
            aria-describedby={instructions ? `${id}-instructions` : undefined}
          >
            <ValidityGroup invalid={invalid}>
              <GroupLayoutWrapper orientation={orientation} unpadded={unpadded}>
                {items.map((item, index) => {
                  if (item.type === "option") {
                    const { value, disabled = false, children, readOnly, testId } = item;

                    return (
                      <SharedCheckbox
                        ref={index === 0 ? ref : undefined}
                        id={`${id}-${index}`}
                        autoFocus={index === 0 && autoFocus === true}
                        key={value}
                        value={valueSet.has(value)}
                        label={children}
                        disabled={disabled || groupDisabled}
                        unpadded={unpadded}
                        readOnly={readOnly || groupReadOnly}
                        onFocus={focusHandler}
                        onBlur={blurHandler}
                        onChange={(checked) => {
                          if (checked === "indeterminate") return;
                          changeHandler(value, checked);
                        }}
                        testId={testId}
                      />
                    );
                  } else if (item.type === "group") {
                    const { label, children } = item;

                    // TODO: someday support n-level deep groups, for no reason other than then they just work.
                    const options = getOptions(children);
                    if (options.length === 0) return null;

                    const selectedCount = options.filter((option) => valueSet.has(option.value)).length;
                    let value: boolean | "indeterminate" = false;
                    if (selectedCount === options.length) {
                      value = true;
                    } else if (selectedCount > 0) {
                      value = "indeterminate";
                    }

                    return (
                      <Fragment key={`${id}-group-${index}`}>
                        <SharedCheckbox
                          ref={index === 0 ? ref : undefined}
                          id={`${id}-group-${index}`}
                          autoFocus={index === 0 && autoFocus === true}
                          value={value}
                          label={label}
                          disabled={options.every(({ disabled = false }) => disabled) || groupDisabled}
                          unpadded={unpadded}
                          readOnly={groupReadOnly}
                          onFocus={focusHandler}
                          onBlur={blurHandler}
                          onChange={(checked, previous) => {
                            if (checked === "indeterminate") return;

                            if (previous === "indeterminate" && options.some(({ disabled = false }) => disabled)) {
                              checked = false;
                            }

                            groupChangeHandler(
                              options.filter(({ disabled = false }) => !disabled).map((option) => option.value),
                              checked,
                            );
                          }}
                        />
                        <Stack spacing="none">
                          {options.map((option, childIndex) => {
                            const { value, disabled = false, children, readOnly = false, testId } = option;

                            return (
                              <SharedCheckbox
                                id={`${id}-group-${index}-${childIndex}`}
                                key={value}
                                value={valueSet.has(value)}
                                label={children}
                                disabled={disabled || groupDisabled}
                                unpadded={unpadded}
                                readOnly={readOnly || groupReadOnly}
                                onFocus={focusHandler}
                                onBlur={blurHandler}
                                onChange={(checked) => {
                                  if (checked === "indeterminate") return;
                                  changeHandler(value, checked);
                                }}
                                testId={testId}
                              />
                            );
                          })}
                        </Stack>
                      </Fragment>
                    );
                  }

                  return null;
                })}
              </GroupLayoutWrapper>
            </ValidityGroup>
          </div>
        </InputContainer>
      </LoadingGroup.Placeholder>
    </StyleModeBoundary>
  );
});
